/* eslint-disable max-len */
/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable no-param-reassign */
/* eslint-disable max-classes-per-file */

import { jsPDF } from 'jspdf';
import autoTable, { CellHookData } from 'jspdf-autotable';
import { Strings } from '../../Resources/Strings/Strings';
import { IDocumentOrderGeneratorEnvironmentSettings } from '../Data/DocumentOrderGeneratorEnvironmentSettings';
import { Currencies } from '../Util/currencies';

import imgNoPresentationImage from '../../Resources/Images/no-presentation-image.png';
import { POSCatalogData, POSCatalogModelData } from '../../common/libraries/modelViewer/Data/POSCatalogData';
import {
  CatalogOrderFileItemCustomAttributeValue,
  CatalogOrderFileRoot,
} from '../../common/libraries/fileFormats/catalogOrder/catalogOrder';

// -------------------------------------------------------------------------
// Internal DTO types to stuff data inside a RowInfo and be able to extract it...
// -------------------------------------------------------------------------
type MainOrderTableRow = {
  index: number;
  description: string;
  keyValLines: string[];
  price: number;
  quantity: number;
  imageBase64: string;
  imageAspectRatio: number;
};

type KeyValueOrderTableRow = {
  index: number,
  key: string;
  value: string;
  keyValLines: string[];
};

// -------------------------------------------------------------------------
// PDF Doc state helper
// -------------------------------------------------------------------------
class DrawHelper {

  public document : jsPDF;
  public pageSizeCX : number;
  public pageSizeCY : number;
  public drawPositionY : number;
  public pageCount: number = 1;
  public currentPage: number = 1;

  public static fontName = 'Helvetica';
  public static fontStyleNormal = 'normal';
  public static fontStyleItalic = 'italic';
  public static fontStyleBold = 'bold';

  constructor(
    document : jsPDF,
    pageSizeCX : number,
    pageSizeCY : number,
    drawPositionY : number,
  ) {
    this.document = document;
    this.pageSizeCX = pageSizeCX;
    this.pageSizeCY = pageSizeCY;
    this.drawPositionY = drawPositionY;
  }

  static CreateDrawHelper(document : jsPDF) {
    return new DrawHelper(
      document,
      document.internal.pageSize.getWidth(),
      document.internal.pageSize.getHeight(),
      this.GetPageMargin(),
    );
  }

  static GetDocument(drawHelper : DrawHelper) {
    return drawHelper.document;
  }

  static GetY(drawHelper : DrawHelper) {
    return drawHelper.drawPositionY;
  }

  static GetPageCX(drawHelper : DrawHelper) {
    return drawHelper.pageSizeCX;
  }

  static GetPageCY(drawHelper: DrawHelper) {
    return drawHelper.pageSizeCY;
  }

  static IncreaseY(drawHelper: DrawHelper, amount : number) {
    drawHelper.drawPositionY += amount;

    return (drawHelper.drawPositionY);
  }

  static AddPage(drawHelper : DrawHelper) {
    drawHelper.document.addPage();
    drawHelper.pageCount++;

    drawHelper.drawPositionY = this.GetPageMargin();
  }

  static GetPageCount(drawHelper : DrawHelper) {
    return drawHelper.pageCount;
  }

  static GetCartItemDescriptionLineHeight() {
    return 7;
  }

  static GetCartItemGroupLineHeight() {
    return 5;
  }

  static GetCartItemPriceHeight() {
    return 7;
  }

  static GetOrderTotalHeight() {
    return 9;
  }

  static GetCartItemBottomMargin() {
    return 6;
  }

  static GetPageMargin() {
    return 30;
  }

  static GetCustomAttributeLineHeight() {
    return 5;
  }

  static GetCustomAttributeNameWidth() {
    return 40;
  }
}

export class OrderDocumentPDFGenerator {

  static _orderPreviewFontSize: number;
  static _orderPreviewImageWidth: number;
  static _orderEndCustNameAttrName: string;

  static async CreateOrderDataDocument(
    title: string,
    orderData: CatalogOrderFileRoot,
    catalog: POSCatalogData,
    strings : Strings,
    environmentSettings : IDocumentOrderGeneratorEnvironmentSettings,
    bShowPrice: boolean,
    orderPreviewFontSize: number,
    orderPreviewImageWidth: number,
    orderEndCustNameAttrName: string,
  ) {
    // Create an A4 document
    // eslint-disable-next-line new-cap
    const doc = new jsPDF({ unit: 'pt', format: 'a4' });

    const drawHelper = DrawHelper.CreateDrawHelper(doc);
    const sImageLocalPath = catalog?.catalogFileInfo?.displayOptions.logoImagePath;
    const sImagePath = sImageLocalPath ? environmentSettings.catalogInfoPath + sImageLocalPath : undefined;

    this._orderPreviewFontSize = orderPreviewFontSize;
    this._orderPreviewImageWidth = orderPreviewImageWidth;
    this._orderEndCustNameAttrName = orderEndCustNameAttrName;

    await this.generatePdf(doc, title, drawHelper, orderData, false, sImagePath, strings);

    // Return as a blob
    return doc.output('blob');
  }

  static async PrintHeaderLogo(
    doc : jsPDF,
    sImagePath: string | undefined,
    firstPage : boolean,
  ) {

    let imageLogo;
    if (sImagePath) {
      imageLogo = sImagePath;
    } else {
      imageLogo = imgNoPresentationImage;
    }

    // Load the image to grab the dimensions
    const img = new Image();
    img.src = imageLogo;
    await img.decode();

    const maxImageSize = firstPage ? 50 : 20;

    let imageDocWidth = 0;
    let imageDocHeight = 0;

    if (img.width > img.height) {
      imageDocWidth = maxImageSize;
      imageDocHeight = 0;
    } else {
      imageDocWidth = 0;
      imageDocHeight = maxImageSize;
    }

    doc.addImage(imageLogo, 'PNG', 20, 20, imageDocWidth, imageDocHeight);
  }

  static PrintTitle(drawHelper : DrawHelper, sText: string) {
    const doc = DrawHelper.GetDocument(drawHelper);
    this.setFont(doc, 10, DrawHelper.fontStyleNormal);
    DrawHelper.IncreaseY(drawHelper, 8);
    doc.text(
      sText,
      DrawHelper.GetPageMargin(),
      drawHelper.drawPositionY,
    );
    DrawHelper.IncreaseY(drawHelper, 6);
  }

  static PrintOrderHeader(drawHelper : DrawHelper, title : string) {
    const doc = DrawHelper.GetDocument(drawHelper);
    this.setFont(doc, 14, DrawHelper.fontStyleNormal);
    doc.text(
      title,
      DrawHelper.GetPageMargin() + 165,
      DrawHelper.GetPageMargin() + 10,
    );
    DrawHelper.IncreaseY(drawHelper, 12);
  }

  static setFont(
    doc : jsPDF,
    fontSize : number | undefined,
    fontStyle : string | undefined,
    color : number[] = [0, 0, 0],
  ) {
    if (fontSize !== undefined) {
      doc.setFontSize(fontSize);
    }
    if (fontStyle !== undefined) {
      doc.setFont(DrawHelper.fontName, fontStyle);
    }
    doc.setTextColor(color[0], color[1], color[2]);
  }

  public static GetContactEmailToUse(order: CatalogOrderFileRoot) : string {

    // First check order session, the regular case
    if (order.orderSessionData.customerEmailAccount) {
      return order.orderSessionData.customerEmailAccount;
    }

    // Second the "internal application case" where there is no session data
    const contactEmailAttribute = order.orderCustomAttributesValues.find((att) => att.attributeKey.toLowerCase() === 'contactemail');
    if (contactEmailAttribute) {
      return contactEmailAttribute.attributeValue;
    }

    return '---';
  }

  public static GetCustomerIdToUse(order: CatalogOrderFileRoot) : string {
    if (order.orderSessionData.customerId) {
      return order.orderSessionData.customerId;
    }

    const customerIdAttribute = order.orderCustomAttributesValues.find((att) => att.attributeKey.toLowerCase() === 'customerid');
    if (customerIdAttribute) {
      return customerIdAttribute.attributeValue;
    }
    return '---'; // An invalide customer id
  }

  public static getLastItemAttributValue(
    orderData: CatalogOrderFileRoot,
    attrKey: string,
  ): CatalogOrderFileItemCustomAttributeValue | undefined {
    let lastAttributeValue: CatalogOrderFileItemCustomAttributeValue | undefined;

    orderData.items.forEach((item) => {
      item.customAttributesValues.attributesValues.forEach((attributeValue) => {
        if (attributeValue.attributeKey.toLowerCase() === attrKey.toLowerCase()) {
          lastAttributeValue = attributeValue;
        }
      });
    });

    if (!lastAttributeValue) {
      return undefined;
    }

    return lastAttributeValue;
  }

  static async buildOrderAttributedableContent(orderData: CatalogOrderFileRoot) : Promise<KeyValueOrderTableRow[]> {

    const rows: KeyValueOrderTableRow[] = [];
    // -------------------------------------------------------------------------
    // 1 Order Session data stuff
    // -------------------------------------------------------------------------
    {
      const keyValLines: string[] = [];
      const customerId = this.GetCustomerIdToUse(orderData);
      if (customerId && customerId !== '---') {
        keyValLines.push(`${customerId}`);
        const customerIdRow : KeyValueOrderTableRow = {
          index: rows.length + 1,
          key: 'Customer Id',
          value: keyValLines.join('\n'),
          keyValLines,
        };
        rows.push(customerIdRow);
      }
    }
    {
      const keyValLines: string[] = [];
      const email = this.GetContactEmailToUse(orderData);
      if (email && email !== '---') {
        keyValLines.push(`${email}`);
        const customerIdRow : KeyValueOrderTableRow = {
          index: rows.length + 1,
          key: 'e-mail',
          value: keyValLines.join('\n'),
          keyValLines,
        };
        rows.push(customerIdRow);
      }
    }

    {
      const keyValLines: string[] = [];
      const endCustomerName = this.getLastItemAttributValue(orderData, this._orderEndCustNameAttrName);
      if (endCustomerName) {
        keyValLines.push(`${endCustomerName.attributeValue}`);
        const customerIdRow : KeyValueOrderTableRow = {
          index: rows.length + 1,
          key: `${endCustomerName.attributeName}`,
          value: keyValLines.join('\n'),
          keyValLines,
        };
        rows.push(customerIdRow);
      }
    }
    // -------------------------------------------------------------------------
    // 2 The extra items (from order custom attributes, the ones with price)
    //     No image. They are to be treated as independent order lines
    //     (i.e. independent articles)
    // -------------------------------------------------------------------------
    orderData.orderCustomAttributesValues.forEach((customAtt) => {
      // -----------------------------------------------------------------------------------------
      // Only the attrubutes without price or quantity are to be selected as order lines
      // (e.g. accessories, socks, etc)
      // All others are "additional order information, like final customer name, etc
      // -----------------------------------------------------------------------------------------
      if (customAtt.attributeBasePrice === 0 && customAtt.attributeEnabled && customAtt.attributeValue !== '') {
        const itemPrice = customAtt.attributeQuantity * customAtt.attributeBasePrice;

        const keyValLines: string[] = [];
        keyValLines.push(`${customAtt.attributeTranslatedValue}`);

        // Build the Row DTO
        const newExtraRow : KeyValueOrderTableRow = {
          index: rows.length + 1,
          key: `${customAtt.attributeName}`,
          value: keyValLines.join('\n'),
          keyValLines,
        };
        rows.push(newExtraRow);
      }
    });

    return rows;
  }

  static async drawwKeyValueTable(keyValueRows: KeyValueOrderTableRow[], doc: jsPDF, drawHelper : DrawHelper, strings: Strings) {
    const fontSize = this._orderPreviewFontSize;
    const lineHeight = fontSize * 1.2;
    const imgColWidth = this._orderPreviewImageWidth;

    // Step 1.2: Generate table
    autoTable(doc, {
      startY: drawHelper.drawPositionY,
      body: keyValueRows,
      rowPageBreak: 'avoid',
      columns: [
        { header: '#', dataKey: 'index' },
        { header: `${strings.REPORT_TITLE_KEY}`, dataKey: 'key' },
        { header: `${strings.REPORT_TITLE_VALUE}`, dataKey: 'value' },
      ],
      styles: {
        fontSize,
        cellPadding: 5,
        valign: 'top',
      },
      columnStyles: {
        index: { cellWidth: 20 },
        key: { cellWidth: (120 - 40) },
        value: { cellWidth: (390 + 40), halign: 'left' },
      },
      theme: 'grid',
      useCss: true,
      margin: { top: 25, left: 25, right: 25 },
      pageBreak: 'auto',
      headStyles: {
        fillColor: [240, 240, 240],
        textColor: [0, 0, 0],
        fontStyle: 'bold',
      },
      alternateRowStyles: { fillColor: [245, 245, 245] },

      // 1.2.1 Remove text from image cell
      didParseCell: (data) => {
        if (data.section === 'body' && data.column.dataKey === 'imageBase64') {
          data.cell.text = [''];
        }
      },

      // 1.2.2. Adjust row height based on image + text height
      willDrawCell: (data) => {
        if (data.section === 'body' && data.column.dataKey === 'description') {
          const row = data.row.raw as KeyValueOrderTableRow;
          const textHeight = row.keyValLines.length * lineHeight;
          data.row.height = Math.max(data.row.height, textHeight);
        }
      },

      // 1.2.3 Draw image with proper aspect ratio
      didDrawCell: (data) => {
        // Save the last position for the next table (if any...)
        if (data.section === 'body') {
          const y = data.cell.y + data.cell.height;
          drawHelper.drawPositionY = Math.max(drawHelper.drawPositionY, y);
        }
      },
    });
  }

  static async buildAndDrawAttributeTable(doc: jsPDF, drawHelper : DrawHelper, orderData: CatalogOrderFileRoot, bShowPrice: boolean, sImagePath: string | undefined, strings: Strings) {
    // Step 1.1: Build main table content
    const orderAttrTableRows: KeyValueOrderTableRow[] = await this.buildOrderAttributedableContent(orderData);
    // Step 1.2: Draw it
    this.PrintTitle(drawHelper, strings.ORDER_DETAIL_TITLE_LABEL);
    await this.drawwKeyValueTable(orderAttrTableRows, doc, drawHelper, strings);
    drawHelper.drawPositionY += 10;
  }

  // Utility to extract image dimensions from base64
  static getImageDimensions(base64: string): Promise<{ width: number; height: number }> {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => resolve({ width: img.width, height: img.height });
      img.onerror = () => resolve({ width: 1, height: 1 }); // fallback
      img.src = base64;
    });
  }

  static async buildOrderMainTableContent(orderData: CatalogOrderFileRoot) : Promise<MainOrderTableRow[]> {
    // -----------------
    // Totalizers
    // -----------------
    let totalPrice = 0;
    let totalItems = 0;

    // -----------------------------------------------------------------
    // 1. Pepare rows incl. image aspect ratio calculated
    // 1.1 The regular order.items (from cartItem configuration)
    // -----------------------------------------------------------------
    const rows: MainOrderTableRow[] = await Promise.all(
      // Transform the order's list of items, in a list of MainOrderTableRow (row content)
      orderData.items.map(async (item, index) => {
        const keyValLines: string[] = [];

        const itemPrice = item.quantity * item.totalPrice;
        totalItems += item.quantity;
        totalPrice += itemPrice;
        const roundedItemPrice = Math.round(itemPrice * 100) / 100;

        // The Article/Product Name
        keyValLines.push(`"${item.name}"`);
        keyValLines.push('---------');

        // ---------------------------------------------------------------------------------
        // The Item's Configuration (sltx's Group/Option pairs) except the hidden ones (N/D)
        // ---------------------------------------------------------------------------------
        item.configuration.parts.sort((a, b) => { return a.groupID - b.groupID; });
        item.configuration.parts.forEach((opt) => {
          if (opt.groupNameTranslated.localeCompare('#N/D') !== 0 && opt.groupNameTranslated.localeCompare('#N/A') !== 0) {
            keyValLines.push(`   ${opt.groupNameTranslated}: ${opt.optionNameTranslated}`);
          }
        });
        keyValLines.push('    -----');
        // ---------------------------------------------------------------------------------
        // The Item's custom attributes (name/value pairs) except the disabled ones (N/D)
        // ---------------------------------------------------------------------------------
        item.customAttributesValues.attributesValues.sort((a, b) => { return a.attributePriority - b.attributePriority; });
        item.customAttributesValues.attributesValues.forEach((attr) => {
          if (attr.attributeEnabled) {
            if (attr.attributeType === 'Separator') {
              keyValLines.push(`   ${attr.attributeName}: ${attr.attributeTranslatedValue}`);
            } else if (attr.attributeTranslatedValue.localeCompare('') !== 0) {
              keyValLines.push(`       ${attr.attributeName}: ${attr.attributeTranslatedValue}`);
            }
          }
        });

        // ----------------------------------------------------------
        // If there's not screen shot save it as a empty string
        // Otherwise extrac apsect ratio to hold to it in the render
        // ----------------------------------------------------------
        const base64 = item.screenshotBase64 ?? '';
        let aspectRatio = 0;

        if (base64.startsWith('data:image')) {
          try {
            const { width, height } = await this.getImageDimensions(base64);
            aspectRatio = height / width;

          } catch {
            aspectRatio = 0;
          }
        }
        // --------------------------------------------
        // Build and return the Row content in our DTO
        // --------------------------------------------
        return {
          index: index + 1,
          description: keyValLines.join('\n'),
          keyValLines,
          price: roundedItemPrice,
          quantity: item.quantity,
          imageBase64: base64,
          imageAspectRatio: aspectRatio,
        };
      }),
    );

    // -------------------------------------------------------------------------
    // 1.1 The extra items (from order custom attributes, the ones with price)
    //     No image. They are to be treated as independent order lines
    //     (i.e. independent articles)
    // -------------------------------------------------------------------------
    orderData.orderCustomAttributesValues.forEach((customAtt) => {
      // -----------------------------------------------------------------------------------------
      // Only the attrubutes with price and quantity are to be selected as order lines
      // (e.g. accessories, socks, etc)
      // All others are "additional order information, like final customer name, etc
      // -----------------------------------------------------------------------------------------
      if (customAtt.attributeBasePrice > 0 && customAtt.attributeQuantity > 0 && customAtt.attributeEnabled) {
        const effectiveValue = customAtt.attributeType !== 'Boolean' ? customAtt.attributeTranslatedValue : '';
        const itemPrice = customAtt.attributeQuantity * customAtt.attributeBasePrice;

        // Feed the totalizers
        totalPrice += itemPrice;
        totalItems += customAtt.attributeQuantity;

        // And we don't want "infinite number strings..."
        const roundedItemPrice = Math.round(itemPrice * 100) / 100;

        const keyValLines: string[] = [];
        if (effectiveValue !== '') {
          keyValLines.push(`${customAtt.attributeName}, ${effectiveValue}`);
        } else {
          keyValLines.push(`${customAtt.attributeName}`);
        }

        // Build the Row DTO
        const newExtraRow : MainOrderTableRow = {
          index: rows.length + 1,
          description: keyValLines.join('\n'),
          keyValLines,
          price: roundedItemPrice,
          quantity: customAtt.attributeQuantity,
          imageBase64: '',
          imageAspectRatio: 0,
        };
        rows.push(newExtraRow);
      }
    });

    // 1.3 Totalizer row
    const keyValLines: string[] = [];
    keyValLines.push('');
    const roundedTotalPrice = Math.round(totalPrice * 100) / 100;
    const newExtraRow : MainOrderTableRow = {
      index: rows.length + 1,
      description: keyValLines.join('\n'),
      keyValLines,
      price: roundedTotalPrice,
      quantity: totalItems,
      imageBase64: '',
      imageAspectRatio: 0,
    };
    rows.push(newExtraRow);

    return rows;
  }

  static async drawwMainTable(orderMainTableRows: MainOrderTableRow[], doc: jsPDF, drawHelper : DrawHelper, bShowPrice: boolean, strings: Strings) {
    const fontSize = this._orderPreviewFontSize;
    const lineHeight = fontSize * 1.2;
    const imgColWidth = this._orderPreviewImageWidth;

    // Make sure the image fits in the row height
    orderMainTableRows.forEach((row) => {
      const requiredAdditionalLines = (row.imageAspectRatio * imgColWidth - lineHeight * row.keyValLines.length) / lineHeight;
      if (requiredAdditionalLines > 0) {
        for (let i = 0; i < (requiredAdditionalLines + 0.5); i++) row.keyValLines.push('');
        row.description = row.keyValLines.join('\n');
      }
    });

    // Step 1.2: Generate table
    autoTable(doc, {
      startY: drawHelper.drawPositionY,
      body: orderMainTableRows,
      rowPageBreak: 'avoid',
      columns: bShowPrice ? [
        { header: '#', dataKey: 'index' },
        { header: `${strings.REPORT_TITLE_DESCRIPTION}`, dataKey: 'description' },
        { header: `${strings.REPORT_TITLE_PRICE}`, dataKey: 'price' },
        { header: `${strings.REPORT_TITLE_QUANTITY}`, dataKey: 'quantity' },
        { header: `${strings.REPORT_TITLE_SCREENSHOT}`, dataKey: 'imageBase64' },
      ] : [
        { header: '#', dataKey: 'index' },
        { header: `${strings.REPORT_TITLE_DESCRIPTION}`, dataKey: 'description' },
        { header: `${strings.REPORT_TITLE_QUANTITY}`, dataKey: 'quantity' },
        { header: `${strings.REPORT_TITLE_SCREENSHOT}`, dataKey: 'imageBase64' },
      ],
      styles: {
        fontSize,
        cellPadding: 5,
        valign: 'top',
      },
      columnStyles: {
        index: { cellWidth: 20 },
        description: { cellWidth: bShowPrice ? (190 - (imgColWidth - 250)) : (225 - (imgColWidth - 250)) },
        price: { cellWidth: 35, halign: 'right' },
        quantity: { cellWidth: 35, halign: 'right' },
        imageBase64: { cellWidth: imgColWidth },
      },
      theme: 'grid',
      useCss: true,
      margin: { top: 25, left: 25, right: 25 },
      pageBreak: 'auto',
      headStyles: {
        fillColor: [240, 240, 240],
        textColor: [0, 0, 0],
        fontStyle: 'bold',
      },
      alternateRowStyles: { fillColor: [245, 245, 245] },

      // 1.2.1 Remove text from image cell
      didParseCell: (data) => {
        if (data.section === 'body' && data.column.dataKey === 'imageBase64') {
          data.cell.text = [''];
        }
      },

      // 1.2.2. Adjust row height based on image + text height
      willDrawCell: (data) => {
        if (data.section === 'body' && data.column.dataKey === 'description') {
          const row = data.row.raw as MainOrderTableRow;
          const textHeight = row.keyValLines.length * lineHeight;
          const scaledImageHeight = row.imageBase64 !== '' ? (imgColWidth - 2) * row.imageAspectRatio : 1;
          const requiredHeight = Math.max(textHeight, scaledImageHeight);
          data.row.height = Math.max(data.row.height, requiredHeight);
        }
      },

      // 1.2.3 Draw image with proper aspect ratio
      didDrawCell: (data) => {

        // Save the last position for the next table (if any...)
        if (data.section === 'body') {
          const y = data.cell.y + data.cell.height;
          drawHelper.drawPositionY = Math.max(drawHelper.drawPositionY, y);
        }

        if (data.section === 'body' && data.column.dataKey === 'imageBase64') {
          const row = data.row.raw as MainOrderTableRow;
          if (!row.imageBase64?.startsWith('data:image')) return;

          const width = imgColWidth;
          const height = (width - 2) * row.imageAspectRatio;
          drawHelper.drawPositionY = data.cell.y + data.cell.height;
          doc.addImage(row.imageBase64, 'PNG', data.cell.x + 1, data.cell.y + 1, width, height);
        }
      },
    });
  }

  static async buildAndDrawMainTable(doc: jsPDF, drawHelper : DrawHelper, orderData: CatalogOrderFileRoot, bShowPrice: boolean, sImagePath: string | undefined, strings: Strings) {
    // Step 1.1: Build main table content
    const orderMainTableRows: MainOrderTableRow[] = await this.buildOrderMainTableContent(orderData);
    // Step 1.2: Draw it
    this.PrintTitle(drawHelper, strings.PRODUCTLIST);
    await this.drawwMainTable(orderMainTableRows, doc, drawHelper, bShowPrice, strings);
    drawHelper.drawPositionY += 10;
  }

  static async generatePdf(doc: jsPDF, title: string, drawHelper : DrawHelper, orderData: CatalogOrderFileRoot, bShowPrice: boolean, sImagePath: string | undefined, strings: Strings) {

    // Step 0. Define first page initial draw position
    drawHelper.drawPositionY = 70;

    // 1. The actual order content
    await this.buildAndDrawAttributeTable(doc, drawHelper, orderData, bShowPrice, sImagePath, strings);

    // 2. The actual order content
    await this.buildAndDrawMainTable(doc, drawHelper, orderData, bShowPrice, sImagePath, strings);

    // ----------------------------------------------------------------------------------
    // In the end add headers and footers. It needs to be after all pages because it is
    // hard to know apriori the number of pages
    // ----------------------------------------------------------------------------------
    const pageCount = doc.getNumberOfPages();
    const { width: pageWidth, height: pageHeight } = doc.internal.pageSize;

    doc.setFontSize(7);
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      if (i === 1) this.PrintOrderHeader(drawHelper, title);
      // eslint-disable-next-line no-await-in-loop
      await this.PrintHeaderLogo(doc, sImagePath, (i === 1));
      const footerText = `Page ${i} of ${pageCount}`;
      const textWidth = doc.getTextWidth(footerText);
      this.setFont(doc, 8, DrawHelper.fontStyleNormal);
      doc.text(footerText, (pageWidth - textWidth) / 2, pageHeight - 20);
    }
  }

}
